import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrashIcon, AlertTriangleIcon, StoreIcon, ExternalLinkIcon, PauseIcon, ShieldIcon, UserIcon, ArrowRightLeftIcon, Loader2Icon, BellIcon } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthProvider';
import { DeleteAccountModal } from '../../components/DeleteAccountModal';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
// Account role types
type AccountRole = 'buyer' | 'seller';
export const AccountActionsSection = () => {
  const navigate = useNavigate();
  const {
    user,
    hasRole,
    switchRole
  } = useAuth();
  // State variables
  const [storeOnline, setStoreOnline] = useState(true);
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState<AccountRole>('buyer');
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showStatusMessage, setShowStatusMessage] = useState(false);
  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;
      try {
        const userDocRef = doc(db, 'users', user.id);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Set store status
          if (userData.storeOnline !== undefined) {
            setStoreOnline(userData.storeOnline);
          }
          // Set current role
          if (userData.activeRole) {
            setCurrentRole(userData.activeRole as AccountRole);
          } else if (user.activeRole) {
            setCurrentRole(user.activeRole as AccountRole);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, [user]);
  // Handle store status toggle
  const toggleStoreStatus = async () => {
    if (!user?.id) return;
    try {
      const userDocRef = doc(db, 'users', user.id);
      await updateDoc(userDocRef, {
        storeOnline: !storeOnline
      });
      setStoreOnline(!storeOnline);
      setStatusMessage(!storeOnline ? 'Your store is now online and visible to buyers' : 'Your store is now offline and hidden from buyers');
      setShowStatusMessage(true);
      setTimeout(() => {
        setShowStatusMessage(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating store status:', error);
      setStatusMessage('Failed to update store status. Please try again.');
      setShowStatusMessage(true);
      setTimeout(() => {
        setShowStatusMessage(false);
      }, 3000);
    }
  };
  // Handle role change (buyer <-> seller only)
  const handleRoleChange = async (newRole: AccountRole) => {
    if (!user?.id || currentRole === newRole) return;
    setIsChangingRole(true);
    try {
      await switchRole(newRole);
      setCurrentRole(newRole);
      setStatusMessage(`Your account role has been changed to ${newRole}.`);
      setShowStatusMessage(true);
    } catch (error) {
      console.error('Error changing role:', error);
      setStatusMessage('Failed to change account role. Please try again.');
      setShowStatusMessage(true);
    } finally {
      setIsChangingRole(false);
      setTimeout(() => {
        setShowStatusMessage(false);
      }, 3000);
    }
  };
  // Handle view store click — matches Seller Dashboard "View Public Profile" exactly
  const handleViewStore = () => {
    if (!user?.id) return;
    navigate(`/seller/${user.id}`);
  };
  return <div data-id="element-3094">
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-3095">
        Account Actions
      </h2>

      {/* Status Message */}
      {showStatusMessage && <div className="mb-6 p-3 bg-[#2DB489]/10 text-[#2DB489] dark:text-[#4ED1A0] rounded-md flex items-start" data-id="element-3096">
          <BellIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3097" />
          <p data-id="element-3098">{statusMessage}</p>
        </div>}

      <div className="space-y-8" data-id="element-3099">
        {/* Store Visibility Section (sellers only) */}
        {hasRole('seller') && <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3100">
            <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3101">
              <div className="flex items-center" data-id="element-3102">
                <StoreIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3103" />
                <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3104">
                  Store Visibility
                </h3>
              </div>
            </div>
            <div className="p-5" data-id="element-3105">
              <div className="flex items-center justify-between mb-4" data-id="element-3106">
                <div data-id="element-3107">
                  <h4 className="text-base font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3108">
                    {storeOnline ? 'Your store is online' : 'Your store is offline'}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3109">
                    {storeOnline ? 'Your store is visible to all buyers' : 'Your store is currently offline and hidden from buyers'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-3110">
                  <input type="checkbox" className="sr-only peer" checked={storeOnline} onChange={toggleStoreStatus} data-id="element-3111" />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-[#1A3330] rounded-full peer peer-focus:ring-2 peer-focus:ring-[#2DB489] dark:peer-focus:ring-[#4ED1A0] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DB489] dark:peer-checked:bg-[#4ED1A0]" data-id="element-3112"></div>
                </label>
              </div>
              {!storeOnline && <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-900/30 rounded-md p-4 mt-4" data-id="element-3113">
                  <div className="flex" data-id="element-3114">
                    <PauseIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2 flex-shrink-0" data-id="element-3115" />
                    <p className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-3116">
                      While your store is offline, buyers cannot see your
                      products or make purchases. You can still access your
                      dashboard and manage your products.
                    </p>
                  </div>
                </div>}
              <div className="mt-6" data-id="element-3117">
                <button onClick={handleViewStore} className="flex items-center px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-[#1A3330] text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-3118">
                  <ExternalLinkIcon className="w-4 h-4 mr-2" data-id="element-3119" />
                  View My Store
                </button>
              </div>
            </div>
          </section>}

        {/* Account Roles Section (buyer/seller only) */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3120">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3121">
            <div className="flex items-center" data-id="element-3122">
              <ArrowRightLeftIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3123" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3124">
                Account Roles
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3125">
            <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-3126">
              Switch between buyer and seller to access different features of
              the platform.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3127">
              <button onClick={() => handleRoleChange('buyer')} disabled={isChangingRole || currentRole === 'buyer'} className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-colors ${currentRole === 'buyer' ? 'border-[#2DB489] dark:border-[#4ED1A0] bg-[#2DB489]/5 dark:bg-[#4ED1A0]/5' : 'border-gray-200 dark:border-[#1A3330] hover:bg-gray-50 dark:hover:bg-[#1A3330]/50'}`} data-id="element-3128">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${currentRole === 'buyer' ? 'bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 text-[#2DB489] dark:text-[#4ED1A0]' : 'bg-gray-100 dark:bg-[#1A3330] text-gray-500 dark:text-[#7A8F8A]'}`} data-id="element-3129">
                  <UserIcon className="w-5 h-5" data-id="element-3130" />
                </div>
                <span className={`font-medium ${currentRole === 'buyer' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-3131">
                  Buyer
                </span>
                <span className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3132">
                  Browse and purchase products
                </span>
              </button>
              <button onClick={() => handleRoleChange('seller')} disabled={isChangingRole || currentRole === 'seller'} className={`flex flex-col items-center justify-center p-4 border rounded-lg transition-colors ${currentRole === 'seller' ? 'border-[#2DB489] dark:border-[#4ED1A0] bg-[#2DB489]/5 dark:bg-[#4ED1A0]/5' : 'border-gray-200 dark:border-[#1A3330] hover:bg-gray-50 dark:hover:bg-[#1A3330]/50'}`} data-id="element-3133">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${currentRole === 'seller' ? 'bg-[#2DB489]/20 dark:bg-[#4ED1A0]/20 text-[#2DB489] dark:text-[#4ED1A0]' : 'bg-gray-100 dark:bg-[#1A3330] text-gray-500 dark:text-[#7A8F8A]'}`} data-id="element-3134">
                  <StoreIcon className="w-5 h-5" data-id="element-3135" />
                </div>
                <span className={`font-medium ${currentRole === 'seller' ? 'text-[#2DB489] dark:text-[#4ED1A0]' : 'text-gray-700 dark:text-[#B0C1BD]'}`} data-id="element-3136">
                  Seller
                </span>
                <span className="text-xs text-gray-500 dark:text-[#7A8F8A] mt-1" data-id="element-3137">
                  Sell products and manage store
                </span>
              </button>
            </div>
            {isChangingRole && <div className="flex justify-center items-center py-2" data-id="element-3138">
                <Loader2Icon className="w-5 h-5 text-[#2DB489] dark:text-[#4ED1A0] animate-spin" data-id="element-3139" />
                <span className="ml-2 text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-3140">
                  Changing role...
                </span>
              </div>}
          </div>
        </section>

        {/* Account Deletion Section */}
        <section className="border border-gray-200 dark:border-[#1A3330] rounded-lg overflow-hidden" data-id="element-3141">
          <div className="bg-gray-50 dark:bg-[#1A3330]/30 px-5 py-4 border-b border-gray-200 dark:border-[#1A3330]" data-id="element-3142">
            <div className="flex items-center" data-id="element-3143">
              <ShieldIcon className="w-5 h-5 text-gray-500 dark:text-[#7A8F8A] mr-2" data-id="element-3144" />
              <h3 className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-3145">
                Account Deletion
              </h3>
            </div>
          </div>
          <div className="p-5" data-id="element-3146">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 rounded-md p-4 mb-6" data-id="element-3147">
              <div className="flex" data-id="element-3148">
                <AlertTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2 flex-shrink-0" data-id="element-3149" />
                <div data-id="element-3150">
                  <h4 className="text-sm font-medium text-red-800 dark:text-red-300" data-id="element-3151">
                    Warning
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-400 mt-1" data-id="element-3152">
                    This will permanently delete your account, products, and
                    data. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <button onClick={() => setDeleteAccountModalOpen(true)} className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white rounded-lg transition-colors" data-id="element-3153">
              <TrashIcon className="w-4 h-4 mr-2" data-id="element-3154" />
              Delete My Account
            </button>
          </div>
        </section>
      </div>

      {/* Delete Account Modal */}
      <DeleteAccountModal isOpen={deleteAccountModalOpen} onClose={() => setDeleteAccountModalOpen(false)} data-id="element-3155" />
    </div>;
};